import React, { useRef, useState } from 'react';

import { Box, MenuItem, MenuList, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FiArchive, FiFolder } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import { IoColorPaletteOutline } from 'react-icons/io5';
import { TbPencil } from 'react-icons/tb';
import { useDrag, useDrop } from 'react-dnd';

import { GridActions } from '@vyce/core/src/components';
import { FormFolderDoc, FormTemplateDoc } from '@vyce/core/src/api/types';
import { FormDoc } from '@vyce/core/src/types';
import { FolderColour, getFolderColourValue } from './folderColour';
import { FolderColourPicker } from './FolderColourPicker';

export const FOLDER_DRAG_TYPE = 'FORM_FOLDER_CARD';

const useStyles = makeStyles(theme => ({
  card: {
    cursor: 'pointer',
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
    willChange: 'transform',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px ${
        theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(20, 20, 43, 0.08)'
      }`,
    },
  },
}));

interface Props {
  folder: FormFolderDoc;
  dragType: string;
  countLabel?: string;
  // The dragged item may be a FormDoc (forms feature) or a FormTemplateDoc
  // (templates feature). Callers typically narrow at the use site.
  onDrop: (item: any, folderId: string) => void;
  onMoveFolderToFolder: (sourceFolderId: string, targetFolderId: string) => void;
  onMoveFolder: (folder: FormFolderDoc) => void;
  onRenameFolder: (folder: FormFolderDoc) => void;
  onArchiveFolder: (folder: FormFolderDoc) => void;
  onDeleteFolder: (folderId: string) => void;
  onColourFolder: (folderId: string, colour: FolderColour | null) => void | Promise<void>;
  onOpenFolder?: (folderId: string) => void;
}

export const FolderCard: React.FC<Props> = ({
  folder,
  dragType,
  countLabel = 'Forms',
  onDrop,
  onMoveFolderToFolder,
  onMoveFolder,
  onRenameFolder,
  onArchiveFolder,
  onDeleteFolder,
  onColourFolder,
  onOpenFolder,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [colourPickerOpen, setColourPickerOpen] = useState(false);

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: FOLDER_DRAG_TYPE,
      item: { folder },
      collect: monitor => ({ isDragging: monitor.isDragging() }),
    }),
    [folder]
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: [dragType, FOLDER_DRAG_TYPE],
      drop: (
        item: { form?: FormDoc; template?: FormTemplateDoc; folder?: FormFolderDoc },
        monitor
      ) => {
        const itemType = monitor.getItemType();
        if (itemType === FOLDER_DRAG_TYPE) {
          const sourceFolder = item.folder;
          if (!sourceFolder || sourceFolder.id === folder.id) return;
          onMoveFolderToFolder(sourceFolder.id, folder.id);
          return;
        }
        const dragged = item.form || item.template;
        if (!dragged?.id) return;
        onDrop(dragged, folder.id);
      },
      canDrop: (
        item: { form?: FormDoc; template?: FormTemplateDoc; folder?: FormFolderDoc },
        monitor
      ) => {
        const itemType = monitor.getItemType();
        if (itemType === FOLDER_DRAG_TYPE) {
          return !!item.folder && item.folder.id !== folder.id;
        }
        return !!(item.form?.id || item.template?.id);
      },
      collect: monitor => ({
        isOver: monitor.isOver() && monitor.canDrop(),
      }),
    }),
    [dragType, folder, onDrop, onMoveFolderToFolder]
  );

  const setRootRef = (node: HTMLDivElement | null) => {
    cardRef.current = node;
    dragRef(node);
    dropRef(node);
  };

  const colourValue = getFolderColourValue(theme, folder.colour);
  const isArchived = folder.archived === true;

  const handleColourSelect = (colour: FolderColour | null) => {
    setColourPickerOpen(false);
    void onColourFolder(folder.id, colour);
  };

  const handleCardClick = () => {
    onOpenFolder?.(folder.id);
  };

  return (
    <div ref={setRootRef} style={{ cursor: 'grab', opacity: isDragging ? 0.4 : 1 }}>
      <Box
        className={classes.card}
        onClick={handleCardClick}
        border={`1px solid ${isOver ? theme.palette.primary.main : colourValue || theme.palette.divider}`}
        borderRadius={12}
        padding={2}
        minHeight={140}
        bgcolor={isOver ? theme.palette.action.selected : theme.palette.background.paper}
        position="relative"
        overflow="hidden">
        {colourValue && (
          <Box position="absolute" top={0} left={0} bottom={0} width={4} bgcolor={colourValue} />
        )}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="flex-start" gridGap={12}>
            <Box
              width={42}
              height={34}
              borderRadius={8}
              bgcolor={colourValue ? `${colourValue}1A` : theme.palette.action.hover}
              display="flex"
              alignItems="center"
              justifyContent="center">
              <FiFolder size={19} color={colourValue || theme.palette.text.secondary} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: 700, marginBottom: 4, color: theme.palette.text.primary }}>
                {folder.name}
              </Typography>
            </Box>
          </Box>

          <Box onClick={e => e.stopPropagation()}>
            <GridActions
              isSmall
              renderEnrichedList={({ closeAnchorEl }) => (
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      onMoveFolder(folder);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiFolder size={14} /> Move
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      onRenameFolder(folder);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <TbPencil size={14} /> Rename
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      onArchiveFolder(folder);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiArchive size={14} /> {isArchived ? 'Unarchive' : 'Archive'}
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      onDeleteFolder(folder.id);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8} color={theme.palette.error.main}>
                      <HiOutlineTrash size={14} /> Delete
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      setColourPickerOpen(true);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <IoColorPaletteOutline size={14} /> Colour
                    </Box>
                  </MenuItem>
                </MenuList>
              )}
            />
          </Box>
        </Box>

        <Box marginTop={2}>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
            {folder.formsCount} {countLabel}
          </Typography>
        </Box>
      </Box>

      <FolderColourPicker
        open={colourPickerOpen}
        anchorEl={cardRef.current}
        currentColour={folder.colour ?? null}
        onClose={() => setColourPickerOpen(false)}
        onSelect={handleColourSelect}
      />
    </div>
  );
};
